import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  LayoutDashboard, ShoppingCart, Package, Boxes, Truck, Scissors,
  Sparkles, Users, DollarSign, ReceiptText, Wand2, Settings as SettingsIcon,
  ChevronsLeft, ChevronsRight, Menu, X, ClipboardList, ArrowRight,
} from "lucide-react";
import DateRangePicker from "./DateRangePicker";
import GlobalCurrencyPicker from "./GlobalCurrencyPicker";
import LanguageToggle from "./LanguageToggle";
import QuickStartGuide from "./QuickStartGuide";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useChannel, CHANNELS } from "@/lib/channel";

const nav = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", color: "#A87552" },
  { to: "/orders", label: "Sales", icon: ShoppingCart, testid: "nav-orders", color: "#C8915A" },
  { to: "/services", label: "Services", icon: Scissors, testid: "nav-services", color: "#D8AAA2" },
  { to: "/products", label: "Retail products", icon: Package, testid: "nav-products", color: "#7D9B7A" },
  { to: "/inventory", label: "Inventory", icon: Boxes, testid: "nav-inventory", color: "#89AEB3" },
  { to: "/vendors", label: "Vendors", icon: Truck, testid: "nav-vendors", color: "#B77B68" },
  { to: "/purchase-orders", label: "Purchase orders", icon: ClipboardList, testid: "nav-pos", color: "#C8915A" },
  { to: "/agencies", label: "Marketing", icon: Sparkles, testid: "nav-agencies", color: "#A18E9F" },
  { to: "/staff", label: "Staff", icon: Users, testid: "nav-staff", color: "#7A9BA0" },
  { to: "/payroll", label: "Payroll", icon: DollarSign, testid: "nav-payroll", color: "#7D9B7A" },
  { to: "/expenses", label: "Expenses", icon: ReceiptText, testid: "nav-expenses", color: "#C8915A" },
  { to: "/insights", label: "AI Insights", icon: Wand2, testid: "nav-insights", color: "#A18E9F" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings", color: "#988E86" },
];

function Brand({ collapsed = false }) {
  return (
    <div className={`flex items-center ${collapsed ? "justify-center" : "gap-2.5"}`}>
      <img src="/opal-beauty-logo.png" alt="Opal Beauty Studio" className="w-10 h-10 object-contain flex-shrink-0" />
      {!collapsed && (
        <div className="min-w-0">
           <div className="font-display font-bold text-[15px] tracking-tight text-[#1C1C1E] leading-none">Luna Accounting Studio</div>
           <div className="text-[10px] uppercase tracking-[0.22em] text-[#8E8E93] mt-1">Salon operations</div>
        </div>
      )}
    </div>
  );
}

function NavItems({ collapsed, onNavigate }) {
  const [searchParams] = useSearchParams();
  const search = searchParams.toString();

  return (
    <nav className={`px-3 flex-1 overflow-y-auto scrollbar-hide ${collapsed ? "py-2" : ""}`}>
      {nav.map(({ to, label, icon: Icon, testid, color }) => (
        <NavLink
          key={to}
          to={search ? `${to}?${search}` : to}
          data-testid={testid}
          onClick={onNavigate}
          className={({ isActive }) =>
            `group flex items-center gap-3 ${collapsed ? "justify-center px-2" : "px-2.5"} py-1.5 rounded-xl text-[13.5px] font-medium mb-0.5 transition-all ${
              isActive
                ? "bg-white text-[#1C1C1E] shadow-[0_1px_2px_rgba(0,0,0,0.04)]"
                : "text-[#3C3C43]/85 hover:bg-white/60"
            }`
          }
          title={collapsed ? label : undefined}
        >
          {({ isActive }) => (
            <>
              <span
                className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors"
                style={{
                  background: isActive ? `${color}18` : "rgba(0,0,0,0.03)",
                  color: isActive ? color : "#3C3C43",
                }}
              >
                <Icon className="w-[16px] h-[16px] stroke-[2]" />
              </span>
              {!collapsed && <span className="truncate">{label}</span>}
            </>
          )}
        </NavLink>
      ))}
    </nav>
  );
}

function useRelativeTime(isoString) {
  const [label, setLabel] = useState("");
  useEffect(() => {
    if (!isoString) { setLabel(""); return; }
    const compute = () => {
      const diffMs = Date.now() - new Date(isoString).getTime();
      const mins = Math.floor(diffMs / 60000);
      if (mins < 1) setLabel("just now");
      else if (mins === 1) setLabel("1 min ago");
      else if (mins < 60) setLabel(`${mins} mins ago`);
      else {
        const hrs = Math.floor(mins / 60);
        setLabel(hrs === 1 ? "1 hour ago" : `${hrs} hours ago`);
      }
    };
    compute();
    const id = setInterval(compute, 60000);
    return () => clearInterval(id);
  }, [isoString]);
  return label;
}

function CRMCard({ collapsed, onGotoSettings }) {
  if (collapsed) return null;
  return (
    <div className="px-3 pb-3">
          <div className="rounded-2xl bg-[#F5E9E5] border border-[#D8AAA2]/45 p-4 relative overflow-hidden">
          <div className="text-[10px] uppercase tracking-[0.16em] text-[#8E8E93] font-medium">CRM integration</div>
          <div className="font-display font-semibold text-[14px] text-[#1C1C1E] mt-1 flex items-center gap-1">
             Connect CRM app <Sparkles className="w-3.5 h-3.5 text-[#B77B68]" />
          </div>
          <div className="text-[11px] mt-1.5 text-[#3C3C43]/70 leading-relaxed">Sync clients, bookings and customer activity when your CRM is ready.</div>
          <button
            data-testid="go-to-settings-btn"
            onClick={onGotoSettings}
            className="mt-3 inline-flex items-center gap-1 rounded-full bg-white/80 border border-black/[0.06] px-2.5 py-1 text-[11.5px] font-medium text-[#1C1C1E] hover:bg-white transition-colors"
          >
             Connection settings <ArrowRight className="w-3 h-3" />
          </button>
        </div>
    </div>
  );
}

export default function AppShell() {
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("luma.collapsed") === "1");
  const [mobileOpen, setMobileOpen] = useState(false);
  const loc = useLocation();
  const navigate = useNavigate();

  useEffect(() => { localStorage.setItem("luma.collapsed", collapsed ? "1" : "0"); }, [collapsed]);
  useEffect(() => { setMobileOpen(false); }, [loc.pathname]);

  const currentLabel = nav.find((n) => n.to === loc.pathname)?.label || "Dashboard";
  const { channel, setChannel } = useChannel();

  return (
    <div className="flex h-[100dvh] app-bg overflow-hidden font-body">
      {/* Desktop Sidebar */}
      <aside
        data-testid="sidebar"
        className={`hidden md:flex sticky top-0 h-[100dvh] ${collapsed ? "w-[76px]" : "w-64"} flex-shrink-0 bg-[#f1ebe3]/85 border-r border-[#6e5142]/[0.10] flex-col transition-[width] duration-300 ease-out`}
      >
        <div className={`${collapsed ? "px-2 pt-5" : "px-5 pt-6"} pb-6 flex items-center justify-between`}>
          <Brand collapsed={collapsed} />
          {!collapsed && (
            <button
              data-testid="collapse-btn"
              onClick={() => setCollapsed(true)}
              className="w-6 h-6 rounded-md flex items-center justify-center text-[#8E8E93] hover:bg-black/5 hover:text-[#1C1C1E] transition-colors"
            >
              <ChevronsLeft className="w-4 h-4" />
            </button>
          )}
        </div>

        {collapsed && (
          <div className="px-2 pb-3">
            <button
              data-testid="expand-btn"
              onClick={() => setCollapsed(false)}
              className="w-full h-8 rounded-lg flex items-center justify-center text-[#8E8E93] hover:bg-black/5 hover:text-[#1C1C1E] transition-colors"
            >
              <ChevronsRight className="w-4 h-4" />
            </button>
          </div>
        )}

        <NavItems collapsed={collapsed} />
        <CRMCard
          collapsed={collapsed}
          onGotoSettings={() => navigate("/settings")}
        />
        <div className="px-5 pb-5 text-[9px] uppercase tracking-[0.18em] text-[#988E86]">Opal Beauty Studio</div>
      </aside>

      {/* Main */}
      <div className="flex-1 h-[100dvh] flex flex-col overflow-hidden min-w-0">
        <header
          data-testid="topbar"
          className="app-topbar h-auto min-h-16 border-b border-black/[0.04] blur-panel flex items-center justify-between gap-3 px-4 py-2 md:px-8 md:py-0 flex-shrink-0 relative z-20"
        >
          <div className="md:hidden flex items-center gap-2">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button data-testid="mobile-menu-btn" variant="ghost" size="icon" className="rounded-xl h-9 w-9" aria-label="Open menu">
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72 p-0 bg-[#F5F3EF] border-black/[0.05]" data-testid="mobile-sidebar">
                <div className="px-5 pt-6 pb-4 flex items-center justify-between">
                  <Brand />
                  <button onClick={() => setMobileOpen(false)} className="w-8 h-8 rounded-lg flex items-center justify-center text-[#8E8E93] hover:bg-black/5">
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <NavItems onNavigate={() => setMobileOpen(false)} />
                <CRMCard
                  onGotoSettings={() => { setMobileOpen(false); navigate("/settings"); }}
                />
                <div className="px-5 py-4 text-[9px] uppercase tracking-[0.18em] text-[#988E86]">Opal Beauty Studio</div>
              </SheetContent>
            </Sheet>
            <div className="font-display font-semibold text-[15px]">{currentLabel}</div>
          </div>

          <div className="hidden md:flex items-center text-[13px] text-[#8E8E93]">
            <span className="text-[#1C1C1E] font-medium">{currentLabel}</span>
            <span className="mx-1.5">/</span>
            <Select value={channel} onValueChange={setChannel}>
              <SelectTrigger
                data-testid="channel-filter"
                className="h-auto border-0 bg-transparent shadow-none px-1 py-0 text-[13px] text-[#8E8E93] font-normal focus:ring-0 focus:ring-offset-0 gap-1 w-auto min-w-0"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent align="start">
                {CHANNELS.map((c) => (
                  <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="topbar-controls ml-auto flex items-center gap-2">
            <LanguageToggle />
            <GlobalCurrencyPicker />
            <DateRangePicker />
          </div>
        </header>
        <main className="flex-1 overflow-auto p-4 sm:p-6 md:p-8 lg:p-9 mobile-main">
          <div className="fade-in-up">
            <Outlet />
          </div>
        </main>
      </div>
      <QuickStartGuide />
    </div>
  );
}
